import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { requisitionsApi, type Requisition, type CreateRequisitionDto, type RequisitionFilters } from '../../../api/requisitions';
import { notifications } from '@mantine/notifications';

export function useRequisitions(filters?: RequisitionFilters) {
  return useQuery({
    queryKey: ['requisitions', filters],
    queryFn: () => requisitionsApi.list(filters),
  });
}

export function useRequisition(id: string | undefined) {
  return useQuery({
    queryKey: ['requisition', id],
    queryFn: () => requisitionsApi.get(id!),
    enabled: !!id,
  });
}

export function useCreateRequisition() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateRequisitionDto) => requisitionsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['requisitions'] });
      notifications.show({
        title: 'Success',
        message: 'Requisition created successfully',
        color: 'green',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.error || 'Failed to create requisition',
        color: 'red',
      });
    },
  });
}

export function useUpdateRequisition() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CreateRequisitionDto> }) =>
      requisitionsApi.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['requisitions'] });
      queryClient.invalidateQueries({ queryKey: ['requisition', variables.id] });
      notifications.show({
        title: 'Success',
        message: 'Requisition updated successfully',
        color: 'green',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.error || 'Failed to update requisition',
        color: 'red',
      });
    },
  });
}

export function useSubmitRequisitionForApproval() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => requisitionsApi.submitForApproval(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['requisitions'] });
      queryClient.invalidateQueries({ queryKey: ['requisition', id] });
      notifications.show({
        title: 'Success',
        message: 'Requisition submitted for approval',
        color: 'green',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.error || 'Failed to submit requisition',
        color: 'red',
      });
    },
  });
}

export function useApproveRequisition() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, comments }: { id: string; comments?: string }) =>
      requisitionsApi.approve(id, comments),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['requisitions'] });
      queryClient.invalidateQueries({ queryKey: ['requisition', variables.id] });
      notifications.show({
        title: 'Success',
        message: 'Requisition approved successfully',
        color: 'green',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.error || 'Failed to approve requisition',
        color: 'red',
      });
    },
  });
}

export function useRejectRequisition() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, comments }: { id: string; comments: string }) =>
      requisitionsApi.reject(id, comments),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['requisitions'] });
      queryClient.invalidateQueries({ queryKey: ['requisition', variables.id] });
      notifications.show({
        title: 'Success',
        message: 'Requisition rejected',
        color: 'yellow',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.error || 'Failed to reject requisition',
        color: 'red',
      });
    },
  });
}

export function useConvertToRFQ() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => requisitionsApi.convertToRFQ(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['requisitions'] });
      queryClient.invalidateQueries({ queryKey: ['requisition', id] });
      notifications.show({
        title: 'Success',
        message: 'Requisition converted to RFQ',
        color: 'green',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.error || 'Failed to convert requisition',
        color: 'red',
      });
    },
  });
}

